import * as React from "react";
import { useMemo } from "react";
import { AlertTriangle } from "lucide-react";
import { Alert, Button, Combobox, SimpleSelect } from "@/components/ui";
import { Form, FormRouteFormType } from "@/services/bedrock";
import { BUILDER_FORM_TYPES, BuilderState, FORM_TYPE_LABELS, formTypeNoun } from "../useFormRouteBuilder";
import { ChoiceCards, Helper, RouteCard, STEP_IDS } from "./RouteCard";

interface Props {
  state: BuilderState;
  forms: Form[];
  hasAccountDefault: boolean;
  canSaveDefault: boolean;
  savingDefault: boolean;
  onChange: (patch: Partial<BuilderState>) => void;
  onSaveDefault: (formId: number) => void;
  onManageDefaults: () => void;
}

const TYPE_DESCRIPTIONS: Partial<Record<FormRouteFormType, string>> = {
  review: "After closing. Asks for a public review.",
  midpoint: "During the loan. Quick private feedback while there's still time to fix things.",
};

interface FormOption {
  value: string;
  label: string;
}

export function FormCardWhichForm({
  state,
  forms,
  hasAccountDefault,
  canSaveDefault,
  savingDefault,
  onChange,
  onSaveDefault,
  onManageDefaults,
}: Props) {
  const formOptions = useMemo<FormOption[]>(
    () => forms.map(form => ({ value: form.guid, label: form.displayName })),
    [forms]
  );
  const label = formTypeNoun(state.formType);

  return (
    <RouteCard step={1} id={STEP_IDS.form} title="Which form opens">
      <ChoiceCards
        horizontal
        label="Which form opens"
        value={state.formMode}
        onChange={formMode => onChange({ formMode })}
        options={[
          {
            value: "type",
            label: "Your default form",
            badge: "Recommended",
            description:
              "Opens the form you've set as the default for each player's region or branch. Change a default anytime and this link keeps working, with no edits to your email templates.",
          },
          {
            value: "specific",
            label: "One specific form",
            description:
              "Always opens the form you pick, for every player. Switching forms later means updating the link in each template.",
          },
        ]}
      />

      {state.formMode === "type" ? (
        <div className="flex flex-col gap-2 rounded-xl bg-slate-50 p-4">
          <span className="text-sm font-medium text-slate-700">Which default?</span>
          <ChoiceCards
            horizontal
            label="Form type"
            value={state.formType}
            onChange={formType => onChange({ formType })}
            options={BUILDER_FORM_TYPES.map(type => ({
              value: type,
              label: FORM_TYPE_LABELS[type],
              description: TYPE_DESCRIPTIONS[type],
            }))}
          />
          <Helper>
            Defaults can differ by region or branch.{" "}
            <Button variant="link" size="inline" onClick={onManageDefaults}>
              Manage Defaults
            </Button>
          </Helper>
        </div>
      ) : (
        <div className="flex flex-col gap-1.5 rounded-xl bg-slate-50 p-4">
          <span className="text-sm font-medium text-slate-700">Form</span>
          <Combobox
            items={formOptions}
            value={formOptions.find(option => option.value === state.formGuid) ?? null}
            onValueChange={(option: FormOption | null) => onChange({ formGuid: option?.value ?? null })}
            searchable
            placeholder="Choose a form"
            emptyText="No published forms"
            isItemEqualToValue={(a: FormOption, b: FormOption) => a?.value === b?.value}
            itemToStringLabel={(option: FormOption) => option?.label ?? ""}
            className="w-full bg-white"
          />
        </div>
      )}

      {state.formMode === "type" && !hasAccountDefault && (
        <Alert variant="warning" data-testid="missing-default">
          <AlertTriangle />
          <Alert.Description className="flex flex-col gap-2">
            <span>No account-wide {label} form is set yet. Choose one now and we'll save it as the default.</span>
            {canSaveDefault ? (
              <div className="rounded-lg border border-amber-200 bg-white px-3 py-1.5 text-sm text-slate-800">
                <SimpleSelect
                  items={forms.map(form => ({ value: String(form.id), label: form.displayName }))}
                  onValueChange={value => value && onSaveDefault(Number(value))}
                  placeholder={savingDefault ? "Saving…" : "Choose a form"}
                  disabled={savingDefault}
                />
              </div>
            ) : (
              <span>Ask a coach to set one in Forms › Manage Defaults.</span>
            )}
          </Alert.Description>
        </Alert>
      )}
    </RouteCard>
  );
}
